import {FieldValues, FormControl, FormField} from 'platform/components';
import {Box, Hide, HStack} from 'platform/foundation';

import {useState} from 'react';
import {Path, PathValue, UseFormReturn, useWatch} from 'react-hook-form';
import {useSelector} from 'react-redux';

import {selectTenant} from '@dms/api/core';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useCalculatePrice} from '../../hooks/useCalculatePrice';
import {useVatRatesOptions} from '../../hooks/useVatRatesOptions';
import {
  LastTouchedFieldType,
  NestedKeyOf,
  PickValidKeys,
  VatDeductibleFormFieldsLabel,
} from './types';

type FormFieldProps<TFieldValues extends FieldValues = FieldValues> = {
  control: FormControl<TFieldValues>;
  formApi: UseFormReturn<TFieldValues>;
  name: PickValidKeys<NestedKeyOf<TFieldValues>>;
  isNotVatDeductible?: boolean;
  labels?: VatDeductibleFormFieldsLabel;
} & TestIdProps;

export function VatDeductibleFormFields<TFieldValues extends FieldValues = FieldValues>(
  props: FormFieldProps<TFieldValues>
) {
  /**
   * alias for better readability
   */
  type InputPath = Path<TFieldValues>;
  type InputValue = PathValue<TFieldValues, Path<TFieldValues>>;

  const [lastTouchedField, setLastTouchedField] = useState<LastTouchedFieldType>();

  /**
   * NOTE:
   * due to complexity and generic type of this component, RHF doesn't recognize,
   * that union of props.name is only a SELECTION of correct union PATH<TFieldValues>
   *
   * props.name union string are handled to ALWAYS include type `priceWithVat.amount`, `priceWoVat.amount` and `vatType`
   */
  // eslint-disable-next-line no-restricted-syntax
  const name = props.name as string as InputPath;
  const priceWithVatPath = `${props.name}.priceWithVat.amount` as InputPath;
  const priceWoVatPath = `${props.name}.priceWoVat.amount` as InputPath;
  const vatTypePath = `${props.name}.vatType` as InputPath;

  const {data: selectedTenant} = useSelector(selectTenant);
  const [vatRatesOptions] = useVatRatesOptions();

  const {priceWoVat, priceWithVat, vatType} = useWatch<TFieldValues>({
    control: props.control,
    name,
  });

  const handleCalculationChange = (res: {withVat?: string} | {withoutVat?: string}) => {
    if ('withVat' in res) {
      props.formApi.setValue(priceWithVatPath, (res.withVat ?? '') as InputValue);
    }
    if ('withoutVat' in res) {
      props.formApi.setValue(priceWoVatPath, (res.withoutVat ?? '') as InputValue);
    }
  };

  useCalculatePrice({
    amount: lastTouchedField === 'withVat' ? priceWithVat?.amount : priceWoVat?.amount,
    countryCode: selectedTenant?.country,
    type: lastTouchedField,
    onChange: handleCalculationChange,
    vatCode: vatType,
  });

  return (
    <HStack width="100%" spacing={3}>
      <Box flex={1}>
        <FormField
          type="number"
          decimalPlaces={2}
          control={props.control}
          name={priceWoVatPath}
          label={props.labels?.priceWoVat ?? i18n.t('general.labels.priceWithoutVat')}
          onChange={() => setLastTouchedField('withoutVat')}
          data-testid={suffixTestId('priceWithoutVat', props)}
        />
      </Box>

      <Hide when={props.isNotVatDeductible}>
        <Box width={25}>
          <FormField
            type="choice"
            name={vatTypePath}
            options={vatRatesOptions}
            control={props.control}
            placeholder=""
            label={props.labels?.vatType ?? i18n.t('general.labels.vat')}
            onChange={() => {
              if (!lastTouchedField) {
                setLastTouchedField('withVat');
              }
            }}
            isNotClearable
            data-testid={suffixTestId('vat', props)}
          />
        </Box>
      </Hide>

      <Box flex={1}>
        <FormField
          type="number"
          decimalPlaces={2}
          control={props.control}
          name={priceWithVatPath}
          label={props.labels?.priceWithVat ?? i18n.t('general.labels.priceWithVat')}
          onChange={() => setLastTouchedField('withVat')}
          data-testid={suffixTestId('priceWithVat', props)}
        />
      </Box>
    </HStack>
  );
}
